import type { AnyAction } from '@reduxjs/toolkit'
import { createApi } from '@reduxjs/toolkit/query/react'
import { RootState } from '..'
import { FORBIDDEN_ERROR_CODE } from '../../commons/constants/constants'
import { captureSentryError, handleError } from '../../commons/utils/utils'
import { setError } from '../slices/errorSlice'
import {
  setMsbAccountIDUploadFlag,
  setTenantID,
  setTenantInfo,
  setTenantName,
  setTenantsList
} from '../slices/tenantSlice'
import { generateRESTQuery, getBaseQuery } from './apiUtils'
import { endpoints as authEndpoints } from './authApi'
import apiConstants, { HTTP_METHODS } from './constants'
import {
  TENANT_SETTINGS_REST_PATHS,
  TENANT_SETTINGS_TAG_TYPES,
  type TenantSettingsPageTag
} from './tenantsApi.constants'
import { translationsApi } from './translationsApi'

const getTenantSettingsInvalidatesTags = (pageTag: TenantSettingsPageTag) => [
  { type: pageTag }
]

export const tenantsApi = createApi({
  reducerPath: 'tenantsApi',
  refetchOnFocus: false,
  baseQuery: getBaseQuery(),
  tagTypes: Object.values(TENANT_SETTINGS_TAG_TYPES),
  endpoints: (builder) => {
    const createTenantSettingsInfoQuery = (
      restPath: string,
      pageTag: TenantSettingsPageTag
    ) =>
      builder.query<GetTenantConfigInfoResponse, { TenantID: string }>({
        query: (requestParams) =>
          generateRESTQuery({
            path: `${apiConstants.TENANTS_REST_URL}${restPath}`,
            queryParams: requestParams
          }),
        providesTags: [{ type: pageTag }],
        async onQueryStarted ({}, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled
            if (data) {
              dispatch(setTenantInfo(data))
            }
          } catch (err: any) {
            captureSentryError(err)
            dispatch(setError())
          }
        }
      })

    const createTenantSettingsEditMutation = <
      RequestType extends { TenantID: string }
    >({
      restPath,
      pageTag,
      shouldInvalidateTranslations = false
    }: {
      restPath: string
      pageTag: TenantSettingsPageTag
      shouldInvalidateTranslations?: boolean
    }) =>
      builder.mutation<unknown, RequestType>({
        query: (requestParams) =>
          generateRESTQuery({
            path: `${apiConstants.TENANTS_REST_URL}${restPath}`,
            method: HTTP_METHODS.PATCH,
            body: requestParams
          }),
        async onQueryStarted (_, { dispatch, queryFulfilled }) {
          if (!shouldInvalidateTranslations) {
            return
          }

          await queryFulfilled
          dispatch(translationsApi.util.invalidateTags(['TenantTranslations']))
        },
        invalidatesTags: getTenantSettingsInvalidatesTags(pageTag)
      })

    return {
      getTenantsList: builder.query<GetTenantsListResponse, {}>({
        query: () =>
          generateRESTQuery({
            path: apiConstants.TENANTS_REST_URL
          }),
        async onQueryStarted ({}, { dispatch, queryFulfilled, getState }) {
          try {
            const { data } = await queryFulfilled
            const state = getState() as RootState
            if (data && data.length > 0) {
              dispatch(setTenantsList(data))
              if (!state.tenant.TenantID) {
                dispatch(setTenantID(data[0].TenantID))
                dispatch(setTenantName(data[0].Name))
              }
            }
          } catch (err: any) {
            captureSentryError(err)
            if (err?.error) {
              if (
                err.error.originalStatus === FORBIDDEN_ERROR_CODE ||
                err.error.status === FORBIDDEN_ERROR_CODE
              ) {
                dispatch(
                  authEndpoints.logout.initiate() as unknown as AnyAction
                )
              }
            }
          }
        }
      }),
      getTenantGeneralInformationInfo: createTenantSettingsInfoQuery(
        TENANT_SETTINGS_REST_PATHS.GENERAL_INFORMATION,
        TENANT_SETTINGS_TAG_TYPES.GENERAL_INFORMATION
      ),
      getTenantOnboardingInfo: createTenantSettingsInfoQuery(
        TENANT_SETTINGS_REST_PATHS.ONBOARDING,
        TENANT_SETTINGS_TAG_TYPES.ONBOARDING
      ),
      getTenantUserStatusInfo: createTenantSettingsInfoQuery(
        TENANT_SETTINGS_REST_PATHS.USER_STATUS,
        TENANT_SETTINGS_TAG_TYPES.USER_STATUS
      ),
      getTenantSurveyInfo: createTenantSettingsInfoQuery(
        TENANT_SETTINGS_REST_PATHS.SURVEY,
        TENANT_SETTINGS_TAG_TYPES.SURVEY
      ),
      getTenantThemeInfo: createTenantSettingsInfoQuery(
        TENANT_SETTINGS_REST_PATHS.THEME,
        TENANT_SETTINGS_TAG_TYPES.THEME
      ),
      getTenantTutorialInfo: createTenantSettingsInfoQuery(
        TENANT_SETTINGS_REST_PATHS.TUTORIAL,
        TENANT_SETTINGS_TAG_TYPES.TUTORIAL
      ),
      getTenantTransitValidationInfo: createTenantSettingsInfoQuery(
        TENANT_SETTINGS_REST_PATHS.TRANSIT_VALIDATION,
        TENANT_SETTINGS_TAG_TYPES.TRANSIT_VALIDATION
      ),
      getTenantAdditionalInformationInfo: createTenantSettingsInfoQuery(
        TENANT_SETTINGS_REST_PATHS.ADDITIONAL_INFORMATION,
        TENANT_SETTINGS_TAG_TYPES.ADDITIONAL_INFORMATION
      ),
      getTenantFriendReferralInfo: createTenantSettingsInfoQuery(
        TENANT_SETTINGS_REST_PATHS.FRIEND_REFERRAL,
        TENANT_SETTINGS_TAG_TYPES.FRIEND_REFERRAL
      ),
      validateTenantInformation: builder.mutation({
        query: (requestParams: TenantValidationParams) =>
          generateRESTQuery({
            path: `${apiConstants.TENANTS_REST_URL}/validate`,
            method: HTTP_METHODS.POST,
            body: requestParams
          })
      }),
      editTenantGeneralInformation:
        createTenantSettingsEditMutation<EditTenantGeneralInfo>({
          restPath: TENANT_SETTINGS_REST_PATHS.GENERAL_INFORMATION,
          pageTag: TENANT_SETTINGS_TAG_TYPES.GENERAL_INFORMATION
        }),
      editTenantSurveyInfo:
        createTenantSettingsEditMutation<EditTenantSurveyInfo>({
          restPath: TENANT_SETTINGS_REST_PATHS.SURVEY,
          pageTag: TENANT_SETTINGS_TAG_TYPES.SURVEY
        }),
      editTenantUserStatusSuspended:
        createTenantSettingsEditMutation<EditTenantUserStatusSuspendedInfo>({
          restPath: '/user-status/suspended',
          pageTag: TENANT_SETTINGS_TAG_TYPES.USER_STATUS
        }),
      editTenantUserStatusOptOutIn:
        createTenantSettingsEditMutation<EditTenantUserStatusOptOutInInfo>({
          restPath: '/user-status/opt-out-in',
          pageTag: TENANT_SETTINGS_TAG_TYPES.USER_STATUS
        }),
      editTenantTutorialStepsInfo:
        createTenantSettingsEditMutation<EditTenantTutorialStepInfo>({
          restPath: '/tutorial/steps',
          pageTag: TENANT_SETTINGS_TAG_TYPES.TUTORIAL
        }),
      editTenantThemeInfo:
        createTenantSettingsEditMutation<EditTenantThemeInfo>({
          restPath: TENANT_SETTINGS_REST_PATHS.THEME,
          pageTag: TENANT_SETTINGS_TAG_TYPES.THEME
        }),
      editTenantOnboardingSignIn:
        createTenantSettingsEditMutation<EditTenantOnboardingSignInInfo>({
          restPath: '/onboarding/sign-in',
          pageTag: TENANT_SETTINGS_TAG_TYPES.ONBOARDING,
          shouldInvalidateTranslations: true
        }),
      editTenantOnboardingSignUp:
        createTenantSettingsEditMutation<EditTenantOnboardingSignUpInfo>({
          restPath: '/onboarding/sign-up',
          pageTag: TENANT_SETTINGS_TAG_TYPES.ONBOARDING,
          shouldInvalidateTranslations: true
        }),
      editTenantOnboardingWaitlist:
        createTenantSettingsEditMutation<EditTenantOnboardingWaitlistInfo>({
          restPath: '/onboarding/waitlist',
          pageTag: TENANT_SETTINGS_TAG_TYPES.ONBOARDING,
          shouldInvalidateTranslations: true
        }),
      editTenantOnboardingProviderConnection:
        createTenantSettingsEditMutation<EditTenantOnboardingProviderConnectionInfo>(
          {
            restPath: '/onboarding/provider-connection',
            pageTag: TENANT_SETTINGS_TAG_TYPES.ONBOARDING,
            shouldInvalidateTranslations: true
          }
        ),
      editTenantSettingsInfo:
        createTenantSettingsEditMutation<EditTenantSettingsInfo>({
          restPath: TENANT_SETTINGS_REST_PATHS.ADDITIONAL_INFORMATION,
          pageTag: TENANT_SETTINGS_TAG_TYPES.ADDITIONAL_INFORMATION
        }),
      getTenantOverviewInfo: builder.query({
        query: (requestParams: { TenantID: string }) =>
          generateRESTQuery({
            path: `${apiConstants.TENANTS_REST_URL}/overview`,
            queryParams: requestParams
          })
      }),
      editTenantReferralPageConfiguration:
        createTenantSettingsEditMutation<EditTenantReferralPageCampaign>({
          restPath: '/friend-referral/page-configuration',
          pageTag: TENANT_SETTINGS_TAG_TYPES.FRIEND_REFERRAL
        }),
      editTenantReferralSettingsConfiguration:
        createTenantSettingsEditMutation<EditTenantReferralSettingsCampaign>({
          restPath: '/friend-referral/settings-configuration',
          pageTag: TENANT_SETTINGS_TAG_TYPES.FRIEND_REFERRAL
        }),
      validateTenantReferralSteps: builder.mutation({
        query: (requestParams: TenantReferralValidationParams) =>
          generateRESTQuery({
            path: `${apiConstants.TENANTS_REST_URL}/friend-referral/validate-steps`,
            method: HTTP_METHODS.POST,
            body: requestParams
          })
      }),
      editTenantTransitValidationProgramSettings:
        createTenantSettingsEditMutation<EditTenantTransitValidationProgramSettings>(
          {
            restPath: '/transit-validation/program-settings',
            pageTag: TENANT_SETTINGS_TAG_TYPES.TRANSIT_VALIDATION
          }
        ),
      editTenantTransitValidationProgramInformation:
        createTenantSettingsEditMutation<EditTenantTransitValidationProgramInformation>(
          {
            restPath: '/transit-validation/program-information',
            pageTag: TENANT_SETTINGS_TAG_TYPES.TRANSIT_VALIDATION
          }
        ),
      validateReferralInstructionStep: builder.mutation({
        query: (requestParams: TenantReferralInstructionValidationparams) =>
          generateRESTQuery({
            path: `${apiConstants.TENANTS_REST_URL}/friend-referral/validate-instruction-steps`,
            method: HTTP_METHODS.POST,
            body: requestParams
          })
      }),
      checkIfMsbAccountIDUploadAllowed: builder.query({
        query: (requestParams: { TenantID: string }) =>
          generateRESTQuery({
            path: `${apiConstants.TENANTS_REST_URL}/msb-account-id-upload-allowed`,
            queryParams: requestParams
          }),
        async onQueryStarted ({}, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled
            if (data !== undefined) {
              dispatch(setMsbAccountIDUploadFlag(Boolean(data)))
            }
          } catch (err: any) {
            handleError(err, dispatch)
          }
        }
      })
    }
  }
})

export const {
  useGetTenantsListQuery,
  useGetTenantGeneralInformationInfoQuery,
  useGetTenantOnboardingInfoQuery,
  useGetTenantUserStatusInfoQuery,
  useGetTenantSurveyInfoQuery,
  useGetTenantThemeInfoQuery,
  useGetTenantTutorialInfoQuery,
  useGetTenantTransitValidationInfoQuery,
  useGetTenantAdditionalInformationInfoQuery,
  useGetTenantFriendReferralInfoQuery,
  useValidateTenantInformationMutation,
  useEditTenantGeneralInformationMutation,
  useEditTenantSurveyInfoMutation,
  useEditTenantUserStatusSuspendedMutation,
  useEditTenantUserStatusOptOutInMutation,
  useEditTenantTutorialStepsInfoMutation,
  useEditTenantThemeInfoMutation,
  useEditTenantOnboardingSignInMutation,
  useEditTenantOnboardingSignUpMutation,
  useEditTenantOnboardingWaitlistMutation,
  useEditTenantOnboardingProviderConnectionMutation,
  useEditTenantSettingsInfoMutation,
  useGetTenantOverviewInfoQuery,
  useEditTenantReferralPageConfigurationMutation,
  useEditTenantReferralSettingsConfigurationMutation,
  useValidateTenantReferralStepsMutation,
  useEditTenantTransitValidationProgramSettingsMutation,
  useEditTenantTransitValidationProgramInformationMutation,
  useValidateReferralInstructionStepMutation,
  useCheckIfMsbAccountIDUploadAllowedQuery
} = tenantsApi
